import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useNavigate } from 'react-router-dom';
// routes
import { PATH_AFTER_LOGIN } from 'src/config-global';
// utils
import axios, { API_ENDPOINTS } from 'src/utils/axios';
import { ROLES } from 'src/constants';
// analytics
import posthog from 'posthog-js';
//
import { mutate } from 'swr';
import { STUDENT_HOME } from 'src/auth/student-microsoft-login';
import { canReturnTo, clearOriginalToken, readOriginalToken, saveOriginalToken } from 'src/auth/impersonation';
import { AuthContext } from './auth-context';
import { isValidToken, jwtDecode, setSession, setDeviceSession } from './utils';

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

const initialState = {
  user: null,
  admin: null,
  loading: true,
  students: null,
  // Set while a super admin is viewing as a student (#605)
  impersonatedBy: null,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      loading: false,
      user: action.payload.user,
      admin: action.payload.admin,
      students: action.payload.students,
      impersonatedBy: action.payload.impersonatedBy || null,
    };
  }
  if (action.type === 'LOGIN') {
    return {
      ...state,
      user: action.payload.user,
      admin: action.payload.admin,
      students: action.payload.students,
      impersonatedBy: action.payload.impersonatedBy || null,
    };
  }
  if (action.type === 'REGISTER') {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      user: null,
      admin: null,
      impersonatedBy: null,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

// Identify the signed-in user in PostHog — except students (#602), who are
// never identified: no identify call, no email. A student session instead
// starts a fresh anonymous identity, so its events cannot link to whoever
// last signed in on this browser. A super admin viewing as a student (#605) is
// treated the same: never identified, as the student or as themselves.
const identifyInAnalytics = (user, impersonatedBy = null) => {
  if (!posthog.__loaded || !user) return;
  if (impersonatedBy || (Array.isArray(user.roles) && user.roles.includes(ROLES.USER_ROLE_STUDENT))) {
    posthog.reset();
    return;
  }
  posthog.identify(user.sub || user._id, {
    email: user.email,
    name: user.name,
    roles: user.roles,
  });
};

const STORAGE_KEY = 'accessToken';
const DEVICE_STORAGE_KEY = 'device-token';

export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reducer, initialState);

  const initialize = useCallback(async () => {
    try {
      // const accessToken = sessionStorage.getItem(STORAGE_KEY);
      const accessToken = localStorage.getItem(STORAGE_KEY);
      const deviceToken = localStorage.getItem(DEVICE_STORAGE_KEY);

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);

        const response = await axios.get(API_ENDPOINTS.auth.me);

        const { user } = response.data;
        // get current route to see if they are on the login screen
        // if they are, redirect them to the dashboard
        // eslint-disable-next-line no-restricted-globals
        const currentRoute = location.pathname;
        if (currentRoute === '/login') navigate(PATH_AFTER_LOGIN, { replace: true });

        // A kept super admin token is only for returning from "view as student"
        if (!user.impersonatedBy) clearOriginalToken();

        identifyInAnalytics(user.user, user.impersonatedBy);

        dispatch({
          type: 'INITIAL',
          payload: {
            user: user.user,
            admin: user.admin,
            students: user.students,
            impersonatedBy: user.impersonatedBy,
          },
        });
      } else if (deviceToken && isValidToken(deviceToken)) {
        setDeviceSession(deviceToken);

        const response = await axios.get(API_ENDPOINTS.auth.me);

        const { user } = response.data;
        console.log('INITIALIZE DEVICE USER', user);

        dispatch({
          type: 'INITIAL',
          payload: {
            user: user.user,
          },
        });
      } else {
        clearOriginalToken();
        dispatch({
          type: 'INITIAL',
          payload: {
            user: null,
            admin: null,
            students: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: 'INITIAL',
        payload: {
          user: null,
          admin: null,
          students: null,
        },
      });
    }
  }, [navigate]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(async (email, password) => {
    const data = {
      email,
      password,
    };

    const response = await axios.post(API_ENDPOINTS.auth.login, data);

    const { accessToken, user } = response.data;

    setSession(accessToken);

    identifyInAnalytics(user);

    dispatch({
      type: 'LOGIN',
      payload: {
        user,
      },
    });
  }, []);

  // Social LOGIN
  const socialLogin = useCallback(async (provider, data) => {
    const response = await axios.post(`${API_ENDPOINTS.auth.login}/${provider}`, data);

    const { accessToken, claims } = response.data;
    setSession(accessToken);

    identifyInAnalytics(claims.user);

    dispatch({
      type: 'LOGIN',
      payload: {
        user: claims.user,
        admin: claims.admin,
        students: claims.students,
      },
    });
  }, []);

  // PAIR DEVICE
  const pairDevice = useCallback(async (pairingCode) => {
    const response = await axios.post(API_ENDPOINTS.auth.pair, {
      pairingCode,
    });

    const { token, claims } = response.data.data;

    setDeviceSession(token);
    localStorage.setItem('device-name', claims.user.name);
    localStorage.setItem('device-id', claims.user.sub);

    dispatch({
      type: 'LOGIN',
      payload: {
        user: claims.user,
      },
    });
  }, []);

  // REGISTER
  const register = useCallback(async (email, password, firstName, lastName) => {
    const data = {
      email,
      password,
      firstName,
      lastName,
    };

    const response = await axios.post(API_ENDPOINTS.auth.register, data);

    const { accessToken, user } = response.data;

    sessionStorage.setItem(STORAGE_KEY, accessToken);

    dispatch({
      type: 'REGISTER',
      payload: {
        user,
      },
    });
  }, []);

  const signOutLocally = useCallback(() => {
    clearOriginalToken();
    // clear all useSWR cache
    // mutate('/api/auth/me', null, false);
    mutate('/api/student/list', [], false);
    setSession(null);
    if (posthog.__loaded) {
      posthog.reset();
    }
    dispatch({
      type: 'LOGOUT',
    });
  }, []);

  // LOGOUT
  const logout = useCallback(async () => {
    // Signing out while viewing as a student ends it too: log the stop
    if (state.impersonatedBy) {
      await axios.post(API_ENDPOINTS.studentImpersonation.stop).catch(() => {});
    }
    signOutLocally();
  }, [signOutLocally, state.impersonatedBy]);

  // VIEW AS STUDENT (#605, dev only, super admins)
  // The server checks everything; this keeps the super admin's own token in
  // this tab so "Return to my account" can put it back.
  const startImpersonation = useCallback(
    async (asn) => {
      const ownToken = localStorage.getItem(STORAGE_KEY);
      const response = await axios.post(API_ENDPOINTS.studentImpersonation.start, { asn });
      const { accessToken, claims } = response.data;

      saveOriginalToken(ownToken);
      // Nothing fetched as the super admin may be shown to the student view
      mutate(() => true, undefined, { revalidate: false });
      setSession(accessToken);
      identifyInAnalytics(claims.user, claims.impersonatedBy);

      dispatch({
        type: 'LOGIN',
        payload: {
          user: claims.user,
          admin: claims.admin,
          students: claims.students,
          impersonatedBy: claims.impersonatedBy,
        },
      });
      navigate(STUDENT_HOME, { replace: true });
    },
    [navigate]
  );

  const stopImpersonation = useCallback(async () => {
    const ownToken = readOriginalToken();
    // Logged on the server; returning does not depend on it
    await axios.post(API_ENDPOINTS.studentImpersonation.stop).catch(() => {});

    let ownClaims = null;
    try {
      ownClaims = ownToken ? jwtDecode(ownToken) : null;
    } catch (_) {
      ownClaims = null;
    }
    clearOriginalToken();
    // Only the super admin's own, unexpired session comes back; otherwise sign out
    if (!canReturnTo(ownClaims, state.impersonatedBy, Date.now() / 1000)) {
      signOutLocally();
      return;
    }

    mutate(() => true, undefined, { revalidate: false });
    setSession(ownToken);
    try {
      const response = await axios.get(API_ENDPOINTS.auth.me);
      const { user } = response.data;
      identifyInAnalytics(user.user, user.impersonatedBy);
      dispatch({
        type: 'LOGIN',
        payload: {
          user: user.user,
          admin: user.admin,
          students: user.students,
          impersonatedBy: user.impersonatedBy,
        },
      });
      navigate(PATH_AFTER_LOGIN, { replace: true });
    } catch (error) {
      console.error(error);
      signOutLocally();
    }
  }, [navigate, signOutLocally, state.impersonatedBy]);

  // UNPAIR DEVICE
  const unpairDevice = useCallback(async () => {
    setDeviceSession(null);
    dispatch({
      type: 'LOGOUT',
    });
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      admin: state.admin,
      impersonatedBy: state.impersonatedBy,
      method: 'jwt',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      //
      login,
      socialLogin,
      pairDevice,
      register,
      logout,
      unpairDevice,
      startImpersonation,
      stopImpersonation,
    }),
    [
      login,
      socialLogin,
      pairDevice,
      logout,
      unpairDevice,
      register,
      startImpersonation,
      stopImpersonation,
      state.user,
      state.admin,
      state.impersonatedBy,
      status,
    ]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};
